import React, { useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { useComplianceCaseContext } from "../../../entrypoints/ComplianceCaseContext";
import { useGetProfileDetailsQuery } from "../../../graphql/supergraphHooks";
import { useGuidedProfileCreation } from "../../../hooks/featureFlags";
import { resolvedAccountType } from "../../../utils/account_types";
import { useUpdateProfile } from "../../profile/hooks";
import { DeclarationProfileType } from "../../profile/ProfileDeclaration/types";
import { JointDetailsForm } from "./JointDetailsForm";
import { createJointFormSchema } from "./JointDetailsForm/schemas";
import { JointFormValues } from "./JointDetailsForm/types";
import { prepareJointAccountPayload, prepareLegacyJointAccountPayload } from "./utils";

export const JointAccountDetails = () => {
  const { caseId, basePath, rootProfileId } = useComplianceCaseContext();
  const { profileId } = useParams<{ profileId: string }>();
  const history = useHistory();
  const guidedProfileCreation = useGuidedProfileCreation();

  const profileResult = useGetProfileDetailsQuery({
    variables: {
      profileId: parseInt(profileId),
      caseId: caseId || 0,
    },
    fetchPolicy: "network-only",
  });

  const closeScreen = useCallback(() => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  }, [history]);

  const isRoot = parseInt(profileId) === rootProfileId;

  const profile = useMemo(
    () => profileResult.data?.complianceProfile,
    [profileResult.data?.complianceProfile],
  );

  const parentProfileId = profile?.parentRelationships?.[0]?.parentProfileId;

  const { updateJoint, updateInstitution, isUpdating } = useUpdateProfile(
    Number(profileId),
    Number(parentProfileId),
    closeScreen,
  );

  const domicileAddress = profile?.addresses?.[0];

  const initialValues: JointFormValues = {
    legalBusinessName: profile?.legalName || "",
    accountType: resolvedAccountType(profile),
    country: domicileAddress?.country?.alpha2code || "",
  };

  const handleFormSubmit = async (values: JointFormValues) => {
    try {
      if (guidedProfileCreation) {
        await updateJoint(prepareJointAccountPayload(caseId, values));
      } else {
        await updateInstitution(
          prepareLegacyJointAccountPayload(caseId, values, profile as DeclarationProfileType),
        );
      }
    } catch {
      toast.error(`Error updating joint profile`);
    }
  };

  return (
    <QueryLoader result={profileResult}>
      <Form
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        yupValidationSchema={createJointFormSchema}
        submitOnlyChangedValues={false}
      >
        <JointDetailsForm onBack={closeScreen} isUpdating={isUpdating} />
      </Form>
    </QueryLoader>
  );
};
